import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const applyThemeAttribute = (theme: Theme) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
};

const readInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage.getItem('theme');
  return saved === 'dark' || saved === 'light' ? saved : 'light';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // 첫 렌더링 시점에 동기적으로 data-theme 을 적용한다.
  // 그래야 자식 컴포넌트의 useEffect(예: PageTodo의 dark 강제 적용)가
  // 부모의 useEffect보다 먼저 실행돼도 덮어쓰임 없이 정상 동작한다.
  const [theme, setThemeState] = useState<Theme>(() => {
    const initial = readInitialTheme();
    applyThemeAttribute(initial);
    return initial;
  });

  const setTheme = (next: Theme) => {
    setThemeState(next);
    localStorage.setItem('theme', next);
    applyThemeAttribute(next);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * 현재 <html data-theme="..."> 속성을 실시간으로 읽는 훅.
 * ThemeContext 상태와 별개로, /todo 처럼 페이지에서 직접 attribute를 덮어쓰는
 * 경우에도 항상 실제 적용된 테마를 반환한다.
 */
export const useResolvedTheme = (): Theme => {
  const read = (): Theme =>
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark'
      ? 'dark'
      : 'light';

  const [resolved, setResolved] = useState<Theme>(read);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setResolved(read());
    const observer = new MutationObserver(() => setResolved(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return resolved;
};
